import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface AccountInfo {
  homeAccountId: string;
  environment: string;
  tenantId: string;
  username: string;
  localAccountId: string;
  name?: string;
  idTokenClaims?: Record<string, any>;
}

export interface AuthState {
  isAuthenticated: boolean;
  account: AccountInfo | null;
  isInitialized: boolean;
  redirectUrl?: string;
}

export interface Me {
  uid: string;
  isAnonymous: boolean;
  displayName: string;
  mail: string;
  jobTitle: string | null;
  department: string;
  manager: string;
  roles: string[];
  userType?: string;
  [key: string]: any;
}

export function isAdmin(me: Me | null | undefined): boolean {
  return environment.enableDebug || (me?.roles?.includes('admin') ?? false);
}

export function isGuest(me: Me | null | undefined): boolean {
  return !me?.userType || me?.userType === 'Guest';
}

export function canSelectRag(_me: Me | null | undefined): boolean {
  return false;
}

export const mockAccount: AccountInfo = {
  homeAccountId: 'mock.home-account',
  environment: 'mock.local',
  tenantId: 'mock-tenant',
  username: 'edgar.dev@pilet-renaud.ch',
  localAccountId: 'mock-local-account',
  name: 'Edgar Dev',
  idTokenClaims: {},
};

export const mockMe: Me = {
  uid: 'mock-user',
  isAnonymous: false,
  displayName: mockAccount.name || 'Edgar Dev',
  mail: mockAccount.username,
  jobTitle: 'Utilisateur mock',
  department: 'PRSA',
  manager: '',
  roles: ['admin'],
  permissions: ['IT', 'DL_Contentieux', 'DL_Responsables'],
  userType: 'Member',
};

export const defaultMe: Me = { isAnonymous: true } as Me;

export const defaultAuthState: AuthState = {
  isAuthenticated: true,
  account: mockAccount,
  isInitialized: true,
};

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly _authState$ = new BehaviorSubject<AuthState>(defaultAuthState);
  private readonly _me$ = new BehaviorSubject<Me>(mockMe);

  public readonly authState$ = this._authState$.asObservable();
  public readonly user$ = this._authState$.asObservable();
  public readonly me$ = this._me$.asObservable();

  constructor(private readonly router: Router) {}

  checkAndSetActiveAccount(): void {
    if (!this._authState$.value.isInitialized) {
      this.login();
    }
  }

  setRedirectUrl(url: string | undefined): void {
    this._authState$.next({
      ...this._authState$.value,
      redirectUrl: url,
    });
  }

  login(): void {
    const redirectUrl = this._authState$.value.redirectUrl;
    this._me$.next(mockMe);
    this._authState$.next({
      isAuthenticated: true,
      account: mockAccount,
      isInitialized: true,
      redirectUrl,
    });
  }

  logout(): void {
    this._me$.next(defaultMe);
    this._authState$.next({
      isAuthenticated: false,
      account: null,
      isInitialized: true,
      redirectUrl: undefined,
    });
    this.router.navigate(['/']);
  }

  me(): Observable<Me> {
    this._me$.next(mockMe);
    return of(mockMe);
  }

  getAccessToken(): Observable<string> {
    return of('mock-access-token');
  }
}
